import { rg4js } from "../Globals/Scripts/RaygunSetup";
import { isRejected, Middleware, UnknownAction } from "@reduxjs/toolkit";
import { FetchBaseQueryMeta } from "@reduxjs/toolkit/query";

type RejectedQueryAction = UnknownAction & {
    meta: {
        // This is made optional because the Front End API does not clone the request object before fetching from the
        // server, so we will not able to set the required FetchBaseQueryMeta.request values.
        baseQueryMeta?: FetchBaseQueryMeta;
    };
};

function isRejectedQueryAction(action: UnknownAction): action is RejectedQueryAction {
    return isRejected(action) && Object.hasOwn(action.meta, 'baseQueryMeta');
}

const SendErrorToRaygunMiddleware: Middleware = (api) => (next) => (action) => {
    if(isRejected(action)) {
        const errorToSend = new Error();
        const thunkName = action.type.replace(/\/rejected$/, "");
        const errorMessageWithSource = `${thunkName}: ${action.error.message}`;

        if(action.payload instanceof Error) {   // this clause matches the case where a developer calls `rejectWithValue` with an Error object, typically from a catch block
            errorToSend.message = action.payload.message ?? errorMessageWithSource;
            errorToSend.stack = action.payload.stack;
        } else if(isRejectedQueryAction(action) && action.meta.baseQueryMeta?.response) {  // this clause matches the case where a RTK query (in our case, GraphQL) is rejected
            errorToSend.message = action.meta.baseQueryMeta.response.statusText || errorMessageWithSource;
            errorToSend.stack = action.meta.baseQueryMeta.response.statusText ? `Status: ${action.meta.baseQueryMeta.response.status}` : undefined;
        } else {  // this is the default case for a rejected action
            errorToSend.message = errorMessageWithSource;
            errorToSend.stack = action.error.stack;
        }

        // Ensure that we have a stack trace and message so that we don't get a generic "Script error" in Raygun.
        errorToSend.stack = errorToSend.stack ?? new Error(`Error with no stack trace available in ${SendErrorToRaygunMiddleware.name}.`).stack;
        errorToSend.message = errorToSend.message ?? `An error was handled by ${SendErrorToRaygunMiddleware.name} without a message.`;

        rg4js('send', {
            error: errorToSend,
            customData: {
                action: action  // log this to aid in future tweaks of this middleware
            }
        });
    }

    return next(action);
};

export default SendErrorToRaygunMiddleware;
